import { exitLabel } from './messagingLabels';
import { Caption } from './ui';
import Pill from '../../Lifecycle/shared/Pill';
import { formatDateTime, formatMoney } from '../../Lifecycle/shared/format';
import {
  channelLabel,
  eventLabel,
  workerEventLine,
  reasonLabel,
} from '../../Lifecycle/shared/labels';

import {
  EventTimelineItem,
  LegacyTimelineItem,
  MessageTimelineItem,
  TimelineItem,
} from '@/services/lifecycleAPI';
import styled from '@/styles';

type Tone = 'green' | 'amber' | 'red' | 'grey' | 'blue';

const Row = styled('div', {
  display: 'grid',
  gridTemplateColumns:
    '170px 80px minmax(200px, 1.2fr) minmax(150px, auto) minmax(260px, 2fr) 90px',
  gap: '12px',
  alignItems: 'start',
  padding: '12px 16px',
  borderTop: '1px solid #EDEDED',
  minWidth: '1000px',
  variants: { future: { true: { backgroundColor: '#FFFCF2' } } },
});

const Stack = styled('span', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2px',
});

const humanize = (value: string) => {
  const text = value.replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const languageLine = (item: MessageTimelineItem): string | null => {
  if (item.locale_fallback) return 'Sent in English (no Spanish text yet)';
  if (item.locale === 'es') return 'Sent in Spanish';
  return null;
};

type View = {
  channel: string;
  label: string;
  sub: string[];
  status: string;
  tone: Tone;
  preview: string;
  cost: string;
  future: boolean;
};

const messageView = (
  item: MessageTimelineItem,
  stepLabel: (key: string) => string,
): View => {
  const channel = item.channel_used || item.channel_planned;
  let status = humanize(item.status);
  let tone: Tone = 'grey';
  if (item.status === 'scheduled' || item.status === 'sending') tone = 'amber';
  if (item.status === 'failed') tone = 'red';
  if (item.status === 'sent') {
    if (item.clicked_at) {
      status = 'Clicked';
      tone = 'green';
    } else if (item.delivered_at) {
      status = 'Delivered';
      tone = 'green';
    }
  }
  if (item.skip_reason) {
    status = `${status} · ${reasonLabel(item.skip_reason).label}`;
  }
  const sub = [
    item.category === 'transactional'
      ? `Transactional · ${item.step_key}`
      : `${item.journey_key} · ${item.step_key}`,
  ];
  if (item.fallback_reason) {
    // Name the other channel only when one carried the message: with none
    // left the row is a skip, and "sent by" would contradict it (QA 61304).
    // graphy also sets channel_used on rows that never went out (shadow,
    // quiet hours, render or link skips, failed sends), so the row must be
    // sent too (review D-03).
    const usedOther =
      item.status === 'sent' &&
      item.channel_used &&
      item.channel_used !== item.channel_planned
        ? `, sent by ${channelLabel(item.channel_used)}`
        : '';
    sub.push(
      `Planned ${channelLabel(item.channel_planned)}: ${reasonLabel(item.fallback_reason).label}${usedOther}`,
    );
  }
  const language = languageLine(item);
  if (language) sub.push(language);
  return {
    channel,
    label: stepLabel(item.step_key),
    sub,
    status,
    tone,
    preview: item.body_preview || '',
    cost:
      channel === 'sms' && item.cost_cents > 0
        ? formatMoney(item.cost_cents)
        : '',
    future: item.status === 'scheduled',
  };
};

const ACTOR_EVENTS = [
  'journeys_paused',
  'journeys_resumed',
  'do_not_contact_changed',
];

// Events whose payload.reason is an engine code, shown with the same words as
// the message row (review RT-09). call_task_closed carries
// journey_ended_<exit reason> (JourneyEnder), paused, do_not_contact or a
// suppression code (review D-04). journeys_paused carries the admin's own
// text and message_failed the provider's error, both shown as they are.
const REASON_CODE_EVENTS = ['message_skipped', 'call_task_closed'];

const codeLabel = (code: string): string => {
  const ended = /^journey_ended_(.+)$/.exec(code);
  return ended
    ? `Journey ended · ${exitLabel(ended[1], ended[1])}`
    : reasonLabel(code).label;
};

const eventReason = (item: EventTimelineItem): unknown => {
  // JourneyEnder writes the reason a journey ended as exit_reason.
  const exit = item.payload?.exit_reason;
  if (item.event_key === 'journey_exited' && typeof exit === 'string') {
    return exitLabel(exit, exit);
  }
  const raw = item.payload?.reason;
  return typeof raw === 'string' && REASON_CODE_EVENTS.includes(item.event_key)
    ? codeLabel(raw)
    : raw;
};

// graphy writes which way it went (payload.do_not_contact); both used to read
// "Do not contact changed", so the audit trail hid it (review D-07).
const doNotContactLabel = (item: EventTimelineItem): string | null => {
  const on = item.payload?.do_not_contact;
  if (item.event_key !== 'do_not_contact_changed' || typeof on !== 'boolean')
    return null;
  return on ? 'Do not contact set' : 'Do not contact removed';
};

const eventView = (item: EventTimelineItem): View => {
  const reason = eventReason(item);
  const known = eventLabel(item.event_key);
  const base =
    doNotContactLabel(item) ??
    (known === item.event_key ? humanize(item.event_key) : known);
  const byActor = item.actor_name && ACTOR_EVENTS.includes(item.event_key);
  const line = workerEventLine(item.event_key, item.payload);
  return {
    channel: 'event',
    label: line ?? (byActor ? `${base} by ${item.actor_name}` : base),
    sub: [item.event_key],
    status: 'Event',
    tone: 'blue',
    preview: !line && typeof reason === 'string' ? `Reason: ${reason}` : '',
    cost: '',
    future: false,
  };
};

const legacyView = (item: LegacyTimelineItem): View => ({
  channel: item.source === 'sms_logs' ? 'sms' : 'push',
  label: item.source === 'sms_logs' ? 'SMS log' : 'Push log',
  sub: [item.sms_type || item.key || item.source],
  status: humanize(item.status),
  tone: 'grey',
  preview: item.body_preview || '',
  cost: '',
  future: false,
});

const viewOf = (
  item: TimelineItem,
  stepLabel: (key: string) => string,
): View => {
  if (item.kind === 'message') return messageView(item, stepLabel);
  if (item.kind === 'event') return eventView(item);
  return legacyView(item);
};

type RowProps = { item: TimelineItem; stepLabel: (key: string) => string };

const TimelineRow = ({ item, stepLabel }: RowProps) => {
  const view = viewOf(item, stepLabel);
  return (
    <Row
      data-testid={`timelineRow-${item.kind}-${item.id}`}
      future={view.future}
    >
      <Caption css={{ fontVariantNumeric: 'tabular-nums' }}>
        {formatDateTime(item.at)}
      </Caption>
      <span>
        {channelLabel(view.channel === 'event' ? 'Event' : view.channel)}
      </span>
      <Stack>
        <strong style={{ fontWeight: 600 }}>{view.label}</strong>
        {view.sub.map((line) => (
          <Caption key={line}>{line}</Caption>
        ))}
      </Stack>
      <span>
        <Pill tone={view.tone}>{view.status}</Pill>
      </span>
      <span style={{ fontSize: 13, color: '#3D3D3D' }}>{view.preview}</span>
      <Caption>{view.cost}</Caption>
    </Row>
  );
};

export default TimelineRow;
